/**
 * The customer quotation as a PDF — `quoflow.api.download_quotation?quotation_name=QTN-…`
 * (Akhil, 2026-10-28, from the Frappe team's curl; not in the Postman collections).
 *
 * TWO segments — `quoflow.api.*`, not the usual `quoflow.quoflow.quoflow.api.*` — so it is not
 * reachable through `bosun.ts`'s `callGet`, and the production proxy allows it by its full name
 * (`server/policy.ts`, `QUOTATION_PDF_METHOD`).
 *
 * Fetched UNCACHED (`downloadAttachmentBlob`, not the cached loader): the PDF is rendered from the
 * quotation as it stands, and a two-minute-old copy would miss the price just edited.
 *
 * Checked to BE a PDF before it is saved. A Frappe error arrives as JSON, and a session that has
 * lapsed as a login page — either one saved as `QTN-….pdf` is a file that will not open, with the
 * reason thrown away.
 */
import { apiBase } from './apiBase'
import { downloadAttachmentBlob } from './attachmentBlob'
import { parseServerMessages } from './errors'

export const QUOTATION_PDF_METHOD = 'quoflow.api.download_quotation'

export function quotationPdfUrl(quotationName: string): string {
  return `${apiBase()}/api/method/${QUOTATION_PDF_METHOD}?quotation_name=${encodeURIComponent(quotationName)}`
}

/** `QTN-2026-00092.pdf` — a slash in a name would be a folder to the browser's save. */
export function quotationPdfName(quotationName: string): string {
  return `${quotationName.replace(/[\\/:*?"<>|]+/g, '-')}.pdf`
}

export async function fetchQuotationPdf(
  quotationName: string,
  load: (url: string) => Promise<Blob> = downloadAttachmentBlob,
): Promise<Blob> {
  const blob = await load(quotationPdfUrl(quotationName))
  const head = await blob.slice(0, 5).text()
  if (head === '%PDF-') return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })

  let reason = ''
  try {
    const body = JSON.parse(await blob.text()) as { _server_messages?: unknown; exception?: unknown; message?: unknown }
    reason = parseServerMessages(body._server_messages)[0] ?? (typeof body.exception === 'string' ? body.exception : '')
  } catch {
    reason = ''
  }
  throw new Error(reason ? `Could not download the PDF: ${reason}` : `The server's answer for ${quotationName} was not a PDF.`)
}
